/** @jsx jsx */
/** @jsxFrag */
import React from 'react';
import { cssMap, jsx, cx } from '@compiled/react';
import { type ValueType, PopupSelect } from '@atlaskit/select';
import { token } from '@atlaskit/tokens';
import { Pressable, Focusable } from '@atlaskit/primitives/compiled';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';

import { Text } from '@atlaskit/primitives';
import { useIntl } from '@atlassian/jira-intl';
import type {
	AllowedOperators,
	OnChangeOperator,
	OperatorOption,
} from '@atlassian/jira-jql-builder-basic-picker/src/common/types.tsx';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import messages from './messages.tsx';

const targetStyles = cssMap({
	root: {
		paddingTop: token('space.0'),
		paddingBottom: token('space.0'),
		paddingLeft: token('space.150'),
		paddingRight: token('space.150'),
		height: '24px',
		display: 'inline-flex',
		justifyContent: 'center',
		alignItems: 'center',
		flexDirection: 'row',
		columnGap: token('space.075'),
		border: `${token('border.width')} solid ${token('color.border')}`,
		borderRadius: token('radius.small'),
		backgroundColor: token('color.background.neutral.subtle'),
		color: token('color.text'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
	},
	wrapper: {
		paddingRight: token('space.100'),
		display: 'inline-flex',
		alignItems: 'center',
	},
	disabled: {
		backgroundColor: token('color.background.input'),
		'&:hover': {
			backgroundColor: token('color.background.input.hovered'),
		},
	},
	selected: {
		borderColor: token('color.border.selected'),
		color: token('color.text.selected'),
		backgroundColor: token('color.background.selected'),
		'&:hover': {
			backgroundColor: token('color.background.selected.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.selected.pressed'),
		},
	},
	invalid: {
		borderColor: token('color.border.danger'),
		color: token('color.text'),
		backgroundColor: token('color.background.danger'),
		'&:hover': {
			backgroundColor: token('color.background.danger.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.danger.pressed'),
		},
	},
});

type Props = {
	allowedOperators: AllowedOperators;
	onChangeOperator?: OnChangeOperator;
	selectedOperator?: OperatorOption;
	isParentPickerOpen: boolean;
	onOpenOperatorPicker: () => void;
	isInvalid: boolean;
};

const TEST_ID_DROPDOWN =
	'jql-builder-basic.common.ui.picker-trigger.trigger-operator.dropdown-operator';

const getOptionLabel = (option: OperatorOption): string => option.ariaLabel;
const formatOptionLabel = (option: OperatorOption): string => option.shortLabel;
const stopPropagation = (event: React.MouseEvent) => event.stopPropagation();
// Prevent closing the main filters modal on escape key
const onKeyDownHandler = (event: React.KeyboardEvent<HTMLDivElement>) => {
	if (event.key === 'Escape') {
		event.stopPropagation();
	}
};

const TriggerOperator = ({
	allowedOperators,
	selectedOperator = allowedOperators[0],
	onChangeOperator,
	isParentPickerOpen: isDisabled,
	onOpenOperatorPicker,
	isInvalid,
}: Props): JSX.Element => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();

	const onChangeOperatorWithAnalytics = (option: ValueType<OperatorOption>) => {
		onChangeOperator?.(option);
		const analyticsEvent = createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'dropdownItem',
		});
		fireUIAnalytics(analyticsEvent, 'operatorSelect', {
			option: option?.value,
			selectType: 'inline',
		});
	};

	const onMenuOpenAnalytics = () => {
		onOpenOperatorPicker?.();

		const analyticsEvent = createAnalyticsEvent({
			action: 'opened',
			actionSubject: 'dropdown',
		});
		fireUIAnalytics(analyticsEvent, 'operatorSelect', {
			selectType: 'inline',
		});
	};

	const getExtraStyle = (isOpen: boolean) => {
		if (isInvalid) {
			return 'invalid';
		}
		if (isOpen) {
			return 'selected';
		}
		if (isDisabled) {
			return 'disabled';
		}
		return null;
	};

	return (
		// onClick stopPropagation needs to be added here as if add to the popup it does not block the target only popup
		<Focusable as="div" role="presentation" xcss={targetStyles.wrapper} onClick={stopPropagation}>
			<PopupSelect
				label={formatMessage(messages.operatorLabel)}
				testId={TEST_ID_DROPDOWN}
				placeholder={formatMessage(messages.operatorLabel)}
				isInvalid={isInvalid}
				options={allowedOperators}
				menuPlacement="bottom"
				popperProps={{
					strategy: 'fixed', // to arrowDown and arrowUp work
					modifiers: [
						// no space between dropdown and trigger button
						{ name: 'offset', options: { offset: [0, 1] } },
						{
							name: 'preventOverflow',
							enabled: false,
						},
					],
				}}
				minMenuWidth="fit-content"
				onMenuOpen={onMenuOpenAnalytics}
				onChange={onChangeOperatorWithAnalytics}
				onKeyDown={onKeyDownHandler}
				getOptionLabel={getOptionLabel}
				formatOptionLabel={formatOptionLabel}
				isDisabled={isDisabled}
				target={({ isOpen, ...triggerProps }) => {
					const extraStyling = getExtraStyle(isOpen);
					return (
						<Pressable
							{...triggerProps}
							xcss={cx(targetStyles.root, extraStyling && targetStyles[extraStyling])}
							isDisabled={isDisabled}
						>
							<Text as="span" color="inherit">
								{selectedOperator?.valueLabel}
							</Text>
							<ChevronDownIcon label="" size="small" />
						</Pressable>
					);
				}}
			/>
		</Focusable>
	);
};

export default TriggerOperator;
